import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { AnglePhotoCapture } from "@/components/driver/angle-photo-capture";
import { InspectionChecklist } from "@/components/driver/inspection-checklist";
import { CapturedMedia } from "@/components/driver/media-capture";
import { BrandColors, Colors } from "@/constants/theme";
import { CONDITION_OPTIONS } from "@/data/inspection-items";
import {
  InspectionTierValue,
  getMissingAngles,
  getMissingAnglesMessage,
  getTierRequirements,
  validateTierInspection,
} from "@/lib/inspection-tier-requirements";
import inspectionService, {
  CreateInspectionData,
  InspectionItem,
} from "@/lib/services/inspections";
import { showToast } from "@/lib/toast";

import { ThemedText } from "@/components/themed-text";
import { BrandButton } from "@/components/ui/brand-button";
import { Loader } from "@/components/ui/loader";
import { formatVehicleLabel } from "@/components/vehicle/vehicle-card-utils";
import {
  invalidateBookingQueries,
  syncBookingFromInspection,
  useBooking,
} from "@/hooks/queries/use-bookings-query";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { showAlert } from "@/lib/alert";
import { getRemoteImageSource } from "@/lib/utils/media-url";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useQueryClient } from "@tanstack/react-query";
import { Image } from "expo-image";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import React, { useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

export default function VehicleInspectionRoute() {
  return (
    <DesktopAppFrame>
      <VehicleInspectionScreen />
    </DesktopAppFrame>
  );
}

function VehicleInspectionScreen() {
  const router = useRouter();
  const qc = useQueryClient();
  const desktop = useDesktopLayout();
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const params = useLocalSearchParams<{
    bookingId: string;
    type: "pickup" | "delivery";
    inspectionTier?: string;
    mode?: "short";
  }>();

  const bookingId = parseInt(params.bookingId, 10);
  const { data: booking } = useBooking(bookingId);
  const inspectionType = params.type;
  const isShortInspection = params.mode === "short";
  const inspectionTier = (params.inspectionTier ??
    "basic") as InspectionTierValue;
  const tierRequirements = getTierRequirements(inspectionTier);

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [inspectionId, setInspectionId] = useState<number | null>(null);
  const [failedMedia, setFailedMedia] = useState<CapturedMedia[]>([]);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  // Step 1: Media
  const [media, setMedia] = useState<CapturedMedia[]>([]);

  // Step 2: Checklist
  const [checklistItems, setChecklistItems] = useState<InspectionItem[]>([]);
  const [fuelLevel, setFuelLevel] = useState<string>("");
  const [odometerReading, setOdometerReading] = useState<number | undefined>();
  const [generalRemarks, setGeneralRemarks] = useState("");
  const [vin, setVin] = useState("");
  const [warningLightsPresent, setWarningLightsPresent] = useState<
    boolean | null
  >(null);
  const [obdFaultCodes, setObdFaultCodes] = useState("");

  const totalSteps = isShortInspection ? 2 : 3;
  const reviewStep = totalSteps;
  const isDark = colorScheme === "dark";
  const cardBackground = isDark ? "#1a2332" : BrandColors.white;

  const stepLabels: Record<number, string> = isShortInspection
    ? { 1: "Photos & Videos", 2: "Review" }
    : { 1: "Photos & Videos", 2: "Exterior", 3: "Review" };

  const angleMedia = media.filter((m) => m.angle);

  const handleMediaAdded = (newMedia: CapturedMedia) => {
    setMedia((prev) => [...prev, newMedia]);
  };

  const handleMediaRemoved = (id: string) => {
    setMedia(media.filter((m) => m.id !== id));
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const missingAngles = getMissingAngles(media);
      const hasVideo = media.some((m) => m.type === "video");

      if (isShortInspection) {
        if (missingAngles.length > 0 && !hasVideo) {
          showAlert(
            "Photos Required",
            `${getMissingAnglesMessage(missingAngles)} Alternatively, record a walk-around video.`,
          );
          return;
        }
      } else {
        const photoCount = media.filter((m) => m.type === "photo").length;

        if (missingAngles.length > 0) {
          showAlert("Photos Required", getMissingAnglesMessage(missingAngles));
          return;
        }

        if (photoCount < tierRequirements.minPhotos) {
          showAlert(
            "Photos Required",
            `Please capture at least ${tierRequirements.minPhotos} photo(s) before proceeding.`,
          );
          return;
        }
      }
    }

    if (currentStep === 2 && !isShortInspection) {
      const validationError = validateTierInspection({
        tier: inspectionTier,
        photoCount: media.filter((m) => m.type === "photo").length,
        media,
        vin,
        warningLightsPresent,
        odometerReading,
        fuelLevel,
        obdFaultCodes: tierRequirements.requireObd
          ? obdFaultCodes
              .split("\n")
              .map((code) => code.trim())
              .filter(Boolean)
          : null,
        checklistItems,
      });

      if (validationError) {
        showAlert("Inspection Incomplete", validationError);
        return;
      }
    }

    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      router.back();
    }
  };

  const handleSubmit = async () => {
    await submitInspection(media);
  };

  const handleRetryFailedUploads = async () => {
    if (!inspectionId || failedMedia.length === 0) {
      return;
    }

    await submitInspection(failedMedia, inspectionId);
  };

  const submitInspection = async (
    mediaToUpload: CapturedMedia[],
    existingInspectionId?: number | null,
  ) => {
    try {
      setLoading(true);
      setUploadProgress({ done: 0, total: mediaToUpload.length });

      let activeInspectionId = existingInspectionId ?? inspectionId;

      if (!activeInspectionId) {
        const inspectionData: CreateInspectionData = isShortInspection
          ? {
              type: inspectionType,
              general_remarks: generalRemarks.trim() || undefined,
            }
          : {
              type: inspectionType,
              vin: vin.trim() || undefined,
              warning_lights_present: warningLightsPresent ?? undefined,
              fuel_level: (fuelLevel ||
                undefined) as CreateInspectionData["fuel_level"],
              odometer_reading: odometerReading,
              general_remarks: generalRemarks,
              obd_fault_codes: tierRequirements.requireObd
                ? obdFaultCodes
                    .split("\n")
                    .map((code) => code.trim())
                    .filter(Boolean)
                : undefined,
              items: checklistItems,
            };

        const inspection = await inspectionService.createInspection(
          bookingId,
          inspectionData,
        );

        activeInspectionId = inspection.id;
        setInspectionId(inspection.id);
      }

      setUploadingMedia(true);

      const results = await inspectionService.uploadAllMedia(
        activeInspectionId,
        mediaToUpload,
        (done, total) => setUploadProgress({ done, total }),
      );

      setUploadingMedia(false);

      if (results.failed.length > 0) {
        setFailedMedia(results.failed);
        showToast.error(
          `${results.failed.length} file(s) failed to upload. Tap Retry to try again.`,
        );
        return;
      }

      setFailedMedia([]);

      await inspectionService.completeInspection(activeInspectionId);

      showToast.success(
        `${inspectionType === "pickup" ? "Pickup" : "Delivery"} inspection completed successfully!`,
      );
      if (booking) {
        syncBookingFromInspection(qc, booking, inspectionType);
      }

      invalidateBookingQueries(qc, bookingId);
      router.back();
    } catch (error: any) {
      console.error("Failed to submit inspection:", error);
      showToast.error(
        error.response?.data?.message || "Failed to submit inspection",
      );
    } finally {
      setLoading(false);
      setUploadingMedia(false);
    }
  };

  const headerBackground = isDark ? theme.background : BrandColors.white;
  const inactiveStepBackground = isDark ? "#2a3444" : BrandColors.lightGray;

  const renderVehicleSummary = () => {
    if (!booking) {
      return null;
    }

    const imageSource = getRemoteImageSource(booking.vehicle_image_path);
    const title = [booking.vehicle_make, booking.vehicle_model]
      .filter(Boolean)
      .map((value) => formatVehicleLabel(value))
      .join(" ");
    const chips = [
      booking.vehicle_registration?.toUpperCase(),
      booking.vehicle_year ? String(booking.vehicle_year) : null,
      booking.vehicle_fuel_type
        ? formatVehicleLabel(booking.vehicle_fuel_type)
        : null,
    ].filter(Boolean) as string[];

    return (
      <View style={styles.desktopVehicle}>
        <View
          style={[
            styles.desktopVehicleImage,
            { backgroundColor: cardBackground },
          ]}
        >
          {imageSource ? (
            <Image
              source={imageSource}
              style={styles.desktopVehicleImageFill}
              contentFit="contain"
            />
          ) : (
            <Ionicons
              name="car-outline"
              size={28}
              color={BrandColors.mediumGray}
            />
          )}
        </View>
        <View style={styles.desktopVehicleText}>
          <ThemedText type="smallSemiBold" numberOfLines={1}>
            {title || "Vehicle"}
          </ThemedText>
          {chips.length > 0 ? (
            <View style={styles.desktopVehicleChips}>
              {chips.map((chip) => (
                <View
                  key={chip}
                  style={[
                    styles.desktopVehicleChip,
                    { backgroundColor: inactiveStepBackground },
                  ]}
                >
                  <ThemedText
                    style={[
                      styles.desktopVehicleChipText,
                      { color: theme.icon },
                    ]}
                    type="extraSmall"
                  >
                    {chip}
                  </ThemedText>
                </View>
              ))}
            </View>
          ) : null}
        </View>
      </View>
    );
  };

  const renderStepIndicator = () => (
    <View style={styles.stepIndicator}>
      {Array.from({ length: totalSteps }, (_, index) => index + 1).map(
        (step) => (
          <View key={step} style={styles.stepItem}>
            <View
              style={[
                styles.stepCircle,
                desktop && styles.desktopStepCircle,
                {
                  backgroundColor:
                    step <= currentStep
                      ? BrandColors.primary
                      : inactiveStepBackground,
                },
              ]}
            >
              <ThemedText
                style={[
                  styles.stepNumber,
                  {
                    color: step <= currentStep ? BrandColors.white : theme.icon,
                  },
                ]}
                type="extraSmallSemiBold"
              >
                {step}
              </ThemedText>
            </View>
            {step < totalSteps && (
              <View
                style={[
                  styles.stepLine,
                  desktop && styles.desktopStepLine,
                  { backgroundColor: inactiveStepBackground },
                ]}
              />
            )}
          </View>
        ),
      )}
    </View>
  );

  const renderHeader = () => (
    <View
      style={[
        styles.header,
        desktop && styles.desktopHeader,
        {
          backgroundColor: headerBackground,
          borderBottomColor: theme.border,
        },
      ]}
    >
      <TouchableOpacity
        onPress={handleBack}
        style={[
          styles.backButtonCircle,
          desktop && styles.desktopBackButtonCircle,
          { backgroundColor: inactiveStepBackground },
        ]}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Ionicons
          name={desktop ? "chevron-back" : "arrow-back"}
          size={20}
          color={theme.text}
        />
      </TouchableOpacity>

      <View style={styles.headerText}>
        <ThemedText
          style={[styles.headerTitle, desktop && styles.desktopHeaderTitle]}
          type="smallSemiBold"
        >
          Vehicle Inspection
        </ThemedText>
        <View style={styles.subtitleRow}>
          <ThemedText
            style={[styles.stepMeta, { color: theme.icon }]}
            type="extraSmall"
          >
            Step {currentStep} of {totalSteps}
          </ThemedText>
          <ThemedText
            style={[styles.stepMeta, { color: theme.icon }]}
            type="extraSmall"
          >
            {" · "}
          </ThemedText>
          <ThemedText style={styles.stepLabel} type="extraSmallSemiBold">
            {stepLabels[currentStep]}
          </ThemedText>
        </View>
      </View>

      {renderStepIndicator()}
    </View>
  );

  const renderStepContent = () => {
    const step = currentStep === reviewStep ? 3 : currentStep;

    switch (step) {
      case 1:
        return (
          <ScrollView
            style={styles.stepContent}
            contentContainerStyle={[
              styles.stepContentContainer,
              desktop && styles.desktopCenteredContent,
            ]}
            showsVerticalScrollIndicator={false}
          >
            <AnglePhotoCapture
              media={angleMedia}
              onMediaAdded={handleMediaAdded}
              onMediaRemoved={handleMediaRemoved}
              subtitle={
                isShortInspection
                  ? "Take a photo of the front, rear, left and right of the vehicle, or record a walk-around video below."
                  : undefined
              }
            />
          </ScrollView>
        );

      case 2:
        return (
          <View style={styles.stepContent}>
            <InspectionChecklist
              items={checklistItems}
              onItemsChange={setChecklistItems}
              fuelLevel={fuelLevel}
              onFuelLevelChange={setFuelLevel}
              odometerReading={odometerReading}
              onOdometerReadingChange={setOdometerReading}
              generalRemarks={generalRemarks}
              onGeneralRemarksChange={setGeneralRemarks}
              requirements={tierRequirements}
              vin={vin}
              onVinChange={setVin}
              warningLightsPresent={warningLightsPresent}
              onWarningLightsPresentChange={setWarningLightsPresent}
              obdFaultCodes={obdFaultCodes}
              onObdFaultCodesChange={setObdFaultCodes}
              vehicleSummary={desktop ? renderVehicleSummary() : undefined}
            />
          </View>
        );

      case 3:
        return (
          <ScrollView
            style={styles.stepContent}
            contentContainerStyle={[
              styles.stepContentContainer,
              desktop && styles.desktopCenteredContent,
            ]}
            showsVerticalScrollIndicator={false}
          >
            <View
              style={
                desktop
                  ? [
                      styles.desktopReviewCard,
                      {
                        backgroundColor: cardBackground,
                        borderColor: theme.border,
                      },
                    ]
                  : undefined
              }
            >
              <View
                style={[
                  styles.reviewSection,
                  desktop && styles.desktopReviewSection,
                  {
                    backgroundColor: cardBackground,
                    borderColor: theme.border,
                  },
                ]}
              >
                <ThemedText
                  style={styles.reviewSectionTitle}
                  type="smallSemiBold"
                >
                  Media
                </ThemedText>
                <ThemedText style={styles.reviewText} type="small">
                  {media.filter((m) => m.type === "photo").length} Photos,{" "}
                  {media.filter((m) => m.type === "video").length} Videos
                </ThemedText>
                <ThemedText
                  style={[styles.conditionCount, { color: theme.icon }]}
                  type="extraSmall"
                >
                  Vehicle angles: {angleMedia.length}/4
                </ThemedText>
              </View>

              {vin ? (
                <View
                  style={[
                    styles.reviewSection,
                    desktop && styles.desktopReviewSection,
                    {
                      backgroundColor: cardBackground,
                      borderColor: theme.border,
                    },
                  ]}
                >
                  <ThemedText
                    style={styles.reviewSectionTitle}
                    type="smallSemiBold"
                  >
                    VIN
                  </ThemedText>
                  <ThemedText style={styles.reviewText} type="small">
                    {vin}
                  </ThemedText>
                </View>
              ) : null}

              {warningLightsPresent != null ? (
                <View
                  style={[
                    styles.reviewSection,
                    desktop && styles.desktopReviewSection,
                    {
                      backgroundColor: cardBackground,
                      borderColor: theme.border,
                    },
                  ]}
                >
                  <ThemedText
                    style={styles.reviewSectionTitle}
                    type="smallSemiBold"
                  >
                    Warning Lights
                  </ThemedText>
                  <ThemedText style={styles.reviewText} type="small">
                    {warningLightsPresent ? "Present" : "Not present"}
                  </ThemedText>
                </View>
              ) : null}

              {fuelLevel ? (
                <View
                  style={[
                    styles.reviewSection,
                    desktop && styles.desktopReviewSection,
                    {
                      backgroundColor: cardBackground,
                      borderColor: theme.border,
                    },
                  ]}
                >
                  <ThemedText
                    style={styles.reviewSectionTitle}
                    type="smallSemiBold"
                  >
                    Fuel Level
                  </ThemedText>
                  <ThemedText style={styles.reviewText} type="small">
                    {fuelLevel.replace("_", " ").toUpperCase()}
                  </ThemedText>
                </View>
              ) : null}

              {odometerReading != null && (
                <View
                  style={[
                    styles.reviewSection,
                    desktop && styles.desktopReviewSection,
                    {
                      backgroundColor: cardBackground,
                      borderColor: theme.border,
                    },
                  ]}
                >
                  <ThemedText
                    style={styles.reviewSectionTitle}
                    type="smallSemiBold"
                  >
                    Odometer reading
                  </ThemedText>
                  <ThemedText style={styles.reviewText} type="small">
                    {odometerReading.toLocaleString()} Miles
                  </ThemedText>
                </View>
              )}

              {isShortInspection ? null : (
                <View
                  style={[
                    styles.reviewSection,
                    desktop && styles.desktopReviewSection,
                    {
                      backgroundColor: cardBackground,
                      borderColor: theme.border,
                    },
                  ]}
                >
                  <ThemedText
                    style={styles.reviewSectionTitle}
                    type="smallSemiBold"
                  >
                    Checklist items
                  </ThemedText>
                  <ThemedText style={styles.reviewText} type="small">
                    {checklistItems.length} Items Inspected
                  </ThemedText>
                  <View
                    style={[
                      styles.conditionSummary,
                      desktop && styles.desktopConditionSummary,
                    ]}
                  >
                    {CONDITION_OPTIONS.map((option) => {
                      const count = checklistItems.filter(
                        (item) => item.condition === option.value,
                      ).length;

                      if (desktop) {
                        return (
                          <View
                            key={option.value}
                            style={[
                              styles.desktopConditionPill,
                              { backgroundColor: `${option.color}26` },
                            ]}
                          >
                            <ThemedText
                              style={[
                                styles.desktopConditionPillText,
                                { color: option.color },
                              ]}
                              type="extraSmall"
                            >
                              {option.label}: {count}
                            </ThemedText>
                          </View>
                        );
                      }

                      if (count === 0) {
                        return null;
                      }

                      return (
                        <ThemedText
                          key={option.value}
                          style={[styles.conditionCount, { color: theme.icon }]}
                          type="extraSmall"
                        >
                          {option.label}: {count}
                        </ThemedText>
                      );
                    })}
                  </View>
                </View>
              )}

              {generalRemarks ? (
                <View
                  style={[
                    styles.reviewSection,
                    desktop && styles.desktopReviewSection,
                    {
                      backgroundColor: cardBackground,
                      borderColor: theme.border,
                    },
                  ]}
                >
                  <ThemedText
                    style={styles.reviewSectionTitle}
                    type="smallSemiBold"
                  >
                    General Remarks
                  </ThemedText>
                  <ThemedText style={styles.reviewText} type="small">
                    {generalRemarks}
                  </ThemedText>
                </View>
              ) : null}
            </View>
          </ScrollView>
        );

      default:
        return null;
    }
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top", "bottom"]}
    >
      <Stack.Screen options={{ headerShown: false }} />
      {(loading || uploadingMedia) && <Loader />}
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoidingView}
      >
        {renderHeader()}

        {renderStepContent()}

        <View
          style={[
            styles.footer,
            desktop && [styles.desktopFooter, { borderTopColor: theme.border }],
            {
              backgroundColor: isDark ? theme.background : BrandColors.white,
            },
          ]}
        >
          <BrandButton
            onPress={handleBack}
            disabled={loading}
            variant="outline"
            style={desktop ? styles.desktopBackBtn : styles.btn}
          >
            Back
          </BrandButton>
          {currentStep < totalSteps ? (
            <BrandButton
              onPress={handleNext}
              disabled={loading}
              style={desktop ? styles.desktopPrimaryBtn : styles.btn}
              icon="chevron-right"
              contentStyle={styles.nextButtonContent}
            >
              {currentStep === 1 && !isShortInspection
                ? "Next: Checklist"
                : "Next: Review"}
            </BrandButton>
          ) : failedMedia.length > 0 ? (
            <BrandButton
              onPress={handleRetryFailedUploads}
              disabled={loading || uploadingMedia}
              style={desktop ? styles.desktopPrimaryBtn : styles.btn}
            >
              {loading || uploadingMedia
                ? uploadProgress.total > 0
                  ? `Uploading ${uploadProgress.done}/${uploadProgress.total}...`
                  : "Retrying..."
                : `Retry failed (${failedMedia.length})`}
            </BrandButton>
          ) : (
            <BrandButton
              onPress={handleSubmit}
              disabled={loading || uploadingMedia}
              style={desktop ? styles.desktopPrimaryBtn : styles.btn}
              icon="chevron-right"
              contentStyle={styles.nextButtonContent}
            >
              {loading || uploadingMedia
                ? uploadProgress.total > 0
                  ? `Uploading ${uploadProgress.done}/${uploadProgress.total}...`
                  : uploadingMedia
                    ? "Uploading media..."
                    : "Submitting..."
                : "Submit Inspection"}
            </BrandButton>
          )}
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
    marginBottom: 16,
  },
  backButtonCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  headerTitle: {
    fontSize: 15,
    lineHeight: 20,
  },
  subtitleRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: 2,
  },
  stepMeta: {
    fontSize: 12,
    lineHeight: 16,
  },
  stepLabel: {
    fontSize: 12,
    lineHeight: 16,
    color: BrandColors.primary,
  },
  stepIndicator: {
    flexDirection: "row",
    alignItems: "center",
  },
  stepItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  stepCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  stepNumber: {
    fontSize: 11,
    lineHeight: 14,
  },
  stepLine: {
    width: 10,
    height: 2,
    marginHorizontal: 3,
    borderRadius: 1,
  },
  stepContent: {
    flex: 1,
  },
  stepContentContainer: {
    paddingBottom: 16,
  },
  stepHeader: {
    alignItems: "center",
    padding: 24,
    marginBottom: 16,
  },
  stepTitle: {
    fontSize: 20,
    fontWeight: "600",
    marginTop: 12,
    marginBottom: 8,
  },
  stepDescription: {
    fontSize: 14,
    color: BrandColors.mediumGray,
    textAlign: "center",
    lineHeight: 20,
  },
  reviewSection: {
    borderWidth: 1,
    padding: 16,
    marginBottom: 12,
    marginHorizontal: 16,
    borderRadius: 12,
  },
  reviewSectionTitle: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 8,
  },
  reviewText: {
    fontSize: 14,
    lineHeight: 20,
  },
  conditionSummary: {
    marginTop: 8,
    gap: 4,
  },
  conditionCount: {
    fontSize: 13,
    textTransform: "capitalize",
  },
  footer: {
    padding: 16,
    flexDirection: "row",
    gap: 16,
    justifyContent: "space-between",
  },
  nextButtonContent: {
    flexDirection: "row-reverse",
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: BrandColors.secondary,
    paddingVertical: 16,
    borderRadius: 8,
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: "600",
    color: BrandColors.white,
  },
  nextButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: BrandColors.primary,
    paddingVertical: 16,
    borderRadius: 8,
  },
  nextButtonText: {
    fontSize: 16,
    fontWeight: "600",
    color: BrandColors.white,
  },
  submitButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: BrandColors.success,
    paddingVertical: 16,
    borderRadius: 8,
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: "600",
    color: BrandColors.white,
  },
  btn: { width: "48%" },
  desktopHeader: {
    paddingHorizontal: 32,
    paddingTop: 24,
    paddingBottom: 16,
    gap: 16,
    marginBottom: 8,
  },
  desktopBackButtonCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  desktopHeaderTitle: {
    fontSize: 20,
    lineHeight: 28,
  },
  desktopStepCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
  },
  desktopStepLine: {
    width: 40,
    marginHorizontal: 4,
  },
  desktopCenteredContent: {
    width: "100%",
    maxWidth: 720,
    alignSelf: "center",
    paddingHorizontal: 16,
  },
  desktopReviewCard: {
    borderWidth: 1,
    borderRadius: 16,
    overflow: "hidden",
  },
  desktopReviewSection: {
    borderWidth: 0,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderRadius: 0,
    marginHorizontal: 0,
    marginBottom: 0,
  },
  desktopConditionSummary: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  desktopConditionPill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  desktopConditionPillText: {
    fontSize: 11,
  },
  desktopFooter: {
    paddingHorizontal: 32,
    borderTopWidth: 1,
  },
  desktopBackBtn: { width: 160 },
  desktopPrimaryBtn: { width: 280 },
  desktopVehicle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  desktopVehicleImage: {
    width: 72,
    height: 56,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  desktopVehicleImageFill: {
    width: "100%",
    height: "100%",
  },
  desktopVehicleText: {
    flex: 1,
    gap: 6,
  },
  desktopVehicleChips: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  desktopVehicleChip: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  desktopVehicleChipText: {
    fontSize: 10,
  },
});
